"use client";
import {
  AppsFlyerStandardEvents,
  CookiesEnum,
  cookiesValues,
  couponCodeKey,
  DEFAULT_INTERNATIONAL_CURRENCY,
  DeviceScreenTypeEnum,
  DeviceTypeEnum,
  DynamicEventNamesPlaceholders,
  EventNamesEnum,
  EventPropertiesEnum,
  LocalStorageKeysEnum,
  PlatformEnum,
  SessionStorageKeysEnum,
  URLQueryParamsEnum,
  UseTrackingAnalyticsSendToEnum,
} from "@/constants/constants";
import { TrackEventParams } from "@/types/helper.types";
import { UserDataInterface } from "@/types/user.types";
import {
  getClientSideCookieValue,
  isMobileDevice,
  getClientPublicIP,
  generateCryptoBasedUUID,
  isTvEnv,
} from "@/utils/common.helpers";
import packageJson from "../../package.json";
import { Analytics, logEvent } from "firebase/analytics";
import { getLocation, isInternationalUser } from "@/utils/location.helpers";
import { useStatsigEventExperiments } from "./useStatsigEventExperiments";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { getUAParser } from "@/utils/lazyUAParser";
import { applyPropertyExclusions } from "@/config/eventPropertyExclusions";

const utmKeys = [
  {
    key: URLQueryParamsEnum.UTM_SOURCE,
    prop: EventPropertiesEnum.UTM_SOURCE,
  },
  {
    key: URLQueryParamsEnum.UTM_MEDIUM,
    prop: EventPropertiesEnum.UTM_MEDIUM,
  },
  {
    key: URLQueryParamsEnum.UTM_CAMPAIGN,
    prop: EventPropertiesEnum.UTM_CAMPAIGN,
  },
];

const clarityEvents = [
  EventNamesEnum.TITLE_PAGE_VIEWED,
  EventNamesEnum.PAYWALL_VIEWED,
  EventNamesEnum.TRIAL_PAYWALL_VIEWED,
  EventNamesEnum.SUBSCRIPTION_PAYWALL_VIEWED,
  EventNamesEnum.TRIAL_INITIATED,
  EventNamesEnum.SUBSCRIPTION_INITIATED,
  EventNamesEnum.LOGIN_INITIATED,
  EventNamesEnum.LOGIN_SUCCESSFUL,
  EventNamesEnum.SIGNUP_SUCCESSFUL,
  EventNamesEnum.TRIAL_PAYMENT_PAGE_VIEWED,
  EventNamesEnum.SUBSCRIPTION_PAYMENT_PAGE_VIEWED,
  EventNamesEnum.TRIAL_PAYMENT_INITIATED,
  EventNamesEnum.SUBSCRIPTION_PAYMENT_INITIATED,
  EventNamesEnum.PAYMENT_INITIATED,
  EventNamesEnum.PAYMENT_INITIATED_SUCCESSFUL,
  EventNamesEnum.PAYMENT_FAILED,
  EventNamesEnum.START_TRIAL,
  EventNamesEnum.CAMPAIGN_CARD_CLICKED,
];

const statsigEvents = [
  EventNamesEnum.START_TRIAL,
  EventNamesEnum.SIGNUP_SUCCESSFUL,
  EventNamesEnum.LOGIN_SUCCESSFUL,
  EventNamesEnum.TRIAL_PAYWALL_VIEWED,
  EventNamesEnum.TRIAL_INITIATED,
  EventNamesEnum.TRIAL_PAYMENT_PAGE_VIEWED,
  EventNamesEnum.TRIAL_PAYMENT_INITIATED,
  EventNamesEnum.SUBSCRIPTION_PAYWALL_VIEWED,
  EventNamesEnum.SUBSCRIPTION_INITIATED,
  EventNamesEnum.SUBSCRIPTION_PAYMENT_PAGE_VIEWED,
  EventNamesEnum.SUBSCRIPTION_PAYMENT_INITIATED,
  EventNamesEnum.PAGE_VIEWED,
];

const appsflyerEvents = [
  EventNamesEnum.SIGNUP_SUCCESSFUL,
  EventNamesEnum.TRIAL_PAYWALL_VIEWED,
  EventNamesEnum.TRIAL_INITIATED,
  EventNamesEnum.TRIAL_PAYMENT_PAGE_VIEWED,
  EventNamesEnum.TRIAL_PAYMENT_INITIATED,
  EventNamesEnum.START_TRIAL,
  EventNamesEnum.SUBSCRIPTION_PAYWALL_VIEWED,
  EventNamesEnum.SUBSCRIPTION_INITIATED,
  EventNamesEnum.SUBSCRIPTION_PAYMENT_PAGE_VIEWED,
  EventNamesEnum.SUBSCRIPTION_PAYMENT_INITIATED,
  EventNamesEnum.APP_OPEN,
  EventNamesEnum.LOGIN_SUCCESSFUL,
];

const getAppendedPlatformEventName = (
  eventName: string,
  platform: string,
): string => {
  return `${eventName}_${platform}`;
};

// Parse document.cookie once into a Map for efficient multi-key lookups
const parseCookiesOnce = (): ((name: string) => string | undefined) => {
  const map = new Map<string, string>();
  if (typeof document !== "undefined") {
    const pairs = document.cookie.split("; ");
    for (let i = 0; i < pairs.length; i++) {
      const eqIdx = pairs[i].indexOf("=");
      if (eqIdx > 0) {
        map.set(pairs[i].substring(0, eqIdx), pairs[i].substring(eqIdx + 1));
      }
    }
  }
  return (name: string) => map.get(name);
};

// Helper function to get fbc/fbp from cookies and set in document, then always fetch from document
const getOrSetFbIdentifiers = (
  cookieLookup?: (name: string) => string | undefined,
) => {
  const getCookie = cookieLookup || getClientSideCookieValue;

  // Check if already set in document
  let fbc = (document as any).__stage_fbc;
  let fbp = (document as any).__stage_fbp;

  // If not set, get from cookies and set in document
  if (!fbc) {
    fbc = getCookie(cookiesValues[CookiesEnum.FBC]);
    (document as any).__stage_fbc = fbc;
  }
  if (!fbp) {
    fbp = getCookie(cookiesValues[CookiesEnum.FBP]);
    (document as any).__stage_fbp = fbp;
  }

  return { fbc, fbp };
};
const getAndSetDeviceId = async (): Promise<string | undefined> => {
  // Hot path: return synchronously when already set (EventsInit pre-initializes this)
  const deviceId = window.deviceId;
  if (deviceId && deviceId !== "") {
    return deviceId;
  }
  // Cold path: async fallback for first call before EventsInit completes
  const { getOrCreateDeviceIdClient } = await import("@/utils/deviceId");
  const newDeviceId = await getOrCreateDeviceIdClient();
  window.deviceId = newDeviceId;
  return newDeviceId;
};
// TODO: Fix this as whenever we use useTrackingAnalytics in any file, it is running useEffect multiple times
export const useTrackingAnalytics = () => {
  const pathname = usePathname();

  const currentLanguage = useMemo(() => {
    return pathname.split("/")[1];
  }, [pathname]);

  // Get all Statsig experiment values dynamically using custom hook
  const statsigExperimentValues = useStatsigEventExperiments();

  // Method to track events
  const trackEvent = async ({
    eventName,
    properties = {},
    sendTo = [],
    restrictDefault = false,
    fbOverRide = {},
    gaOverRide = {},
    addCurrency = false,
  }: TrackEventParams) => {
    try {
      // Parse all cookies once for this trackEvent call
      const cookieLookup = parseCookiesOnce();

      // Skip all tracking in webview — Flutter handles analytics
      const isWebview =
        cookieLookup(cookiesValues[CookiesEnum.IsWebview]) === "true";
      if (isWebview) return;
      if (typeof window.isMobile === "undefined") {
        // to avoid unnecessary calls to isMobileDevice
        window.isMobile = isMobileDevice();
      }
      let parsedUserObj: UserDataInterface = {} as UserDataInterface;
      const parser = await getUAParser();
      const device = parser.getDevice();
      const isMobile = window.isMobile;
      const deviceScreenType = window.isMobile
        ? DeviceScreenTypeEnum.MOBILE
        : DeviceScreenTypeEnum.DESKTOP;
      let platform: PlatformEnum = PlatformEnum.WEB;
      if (device.type === "smarttv") {
        platform = PlatformEnum.TV;
      }

      // attach client ip (cached in sessionStorage) using common helper
      try {
        const ip = await getClientPublicIP();
        if (ip) {
          (properties as Record<string, unknown>)[
            EventPropertiesEnum.IP_ADDRESS
          ] = ip;
        }
      } catch {}

      try {
        parsedUserObj = await JSON.parse(
          decodeURIComponent(
            cookieLookup(cookiesValues[CookiesEnum.UserData]) || "%7B%7D", // encoded "{}"
          ),
        );
        let userId = parsedUserObj?.userId || "";
        const mobileNumber = parsedUserObj?.mobileNumber || "";

        // Fallback: pick userId from query params for watchWithoutLogin flow
        if (!userId) {
          const qpUserId = new URLSearchParams(window.location.search).get(
            URLQueryParamsEnum.USER_ID,
          );
          if (qpUserId) {
            userId = qpUserId;
          }
        }
        const user_id = userId;
        const os = parser.getOS();
        properties["os_name"] = os.name;
        properties["os_version"] = os.version;
        properties["version"] = window.appVersion || packageJson.version;
        properties["device_id"] = await getAndSetDeviceId();
        properties[EventPropertiesEnum.CUSTOMER_USER_ID] = userId;
        properties[EventPropertiesEnum.CURRENCY] =
          sessionStorage.getItem(SessionStorageKeysEnum.CURRENCY) ||
          (isInternationalUser() ? DEFAULT_INTERNATIONAL_CURRENCY : "INR");
        properties[EventPropertiesEnum.APP_LANGUAGE] = currentLanguage;

        const locationInfo = getLocation();
        properties[EventPropertiesEnum.COUNTRY] = locationInfo.country;
        properties[EventPropertiesEnum.CITY] = locationInfo.city;

        const sourceContentSlug = localStorage.getItem(
          LocalStorageKeysEnum.SOURCE_CONTENT_SLUG_BEFORE_PAYING,
        );

        // ref: https://stagedotin.slack.com/archives/C080EJU9873/p1765950032771879
        let firstContentSlug = localStorage.getItem(
          LocalStorageKeysEnum.FIRST_CONTENT_SLUG_BEFORE_PAYING,
        );

        if (sourceContentSlug && !firstContentSlug) {
          firstContentSlug = sourceContentSlug;
          localStorage.setItem(
            LocalStorageKeysEnum.FIRST_CONTENT_SLUG_BEFORE_PAYING,
            sourceContentSlug,
          );
        }

        if (firstContentSlug) {
          // Only set acquisition content_id if event doesn't provide its own
          // (e.g., THUMBNAIL_VIEWED passes content._id from component)
          if (
            !Object.prototype.hasOwnProperty.call(
              properties,
              EventPropertiesEnum.CONTENT_ID,
            )
          ) {
            properties[EventPropertiesEnum.CONTENT_ID] = firstContentSlug;
          }
          // ref: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_to_cart
          properties[EventPropertiesEnum.ECOMMERCE] = {
            items: [{ [EventPropertiesEnum.ITEM_ID]: firstContentSlug }],
          };
          properties[EventPropertiesEnum.CONTENT_IDS] = firstContentSlug;
        }

        if (sourceContentSlug) {
          properties[EventPropertiesEnum.LATEST_CONTENT_ID] = sourceContentSlug;
        }

        const user_culture = cookieLookup(
          cookiesValues[CookiesEnum.UserCulture],
        );
        let content_culture = cookieLookup(cookiesValues[CookiesEnum.Dialect]);
        const initial_user_culture = cookieLookup(
          cookiesValues[CookiesEnum.InitialUserCulture],
        );

        if (!content_culture) {
          content_culture = cookieLookup(
            cookiesValues[CookiesEnum.TempCulture],
          );
        }
        properties["dialect"] = content_culture;
        if (eventName.includes(DynamicEventNamesPlaceholders.USER_CULTURE)) {
          if (user_culture) {
            eventName = eventName.replace(
              DynamicEventNamesPlaceholders.USER_CULTURE,
              user_culture,
            );
          } else {
            // drop this event
            return;
          }
        }

        if (addCurrency) {
          const currency = sessionStorage.getItem(
            SessionStorageKeysEnum.CURRENCY,
          );
          const payingPrice = sessionStorage.getItem(
            SessionStorageKeysEnum.PAYING_PRICE,
          );
          if (currency) {
            properties["currency"] = currency;
          }
          if (payingPrice) {
            properties[EventPropertiesEnum.VALUE] = payingPrice;
            // ref: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_to_cart
            if (!properties[EventPropertiesEnum.ECOMMERCE]) {
              properties[EventPropertiesEnum.ECOMMERCE] = {};
            }
            const ecommerce = properties[
              EventPropertiesEnum.ECOMMERCE
            ] as Record<string, unknown>;
            ecommerce[EventPropertiesEnum.VALUE] = payingPrice;
          }
        }

        if (user_culture && content_culture) {
          properties["user_culture"] = user_culture;
          properties["content_culture"] = content_culture.slice(0, 3);
        }

        if (initial_user_culture) {
          properties["initial_user_culture"] = initial_user_culture;
        }

        // Fetch UTM parameters from sessionStorage or query params and add to properties
        let searchParams: URLSearchParams | null = null;
        utmKeys.forEach(({ key, prop }) => {
          let value = sessionStorage.getItem(key);

          // If not in sessionStorage, check URL and set it
          if (!value) {
            if (!searchParams) {
              searchParams = new URLSearchParams(window.location.search);
            }
            value = searchParams.get(key);
            if (value) {
              sessionStorage.setItem(key, value);
            }
          }

          // Always use the value from sessionStorage (if available)
          if (value) {
            properties[prop] = decodeURIComponent(value);
          }
        });

        properties["device_type"] = isMobile
          ? DeviceTypeEnum.MOBILE
          : DeviceTypeEnum.DESKTOP;

        if (mobileNumber) {
          try {
            // let hashedPhoneNumber = await hashMobileNumber({
            //   mobileNumber: parsedUserObj?.mobileNumber || "",
            // });
            // properties["ph"] = hashedPhoneNumber;

            properties["ph"] = "91" + mobileNumber;
            properties["external_id"] = userId;
          } catch (error) {
            console.error("Error in hashing mobile number", error);
          }
        }

        properties[EventPropertiesEnum.EVENT_ID] = generateCryptoBasedUUID();

        properties = { ...properties, userId, mobileNumber, user_id };
      } catch (error) {
        console.error("Error in getting userId", error);
      }

      //common Properties for all events
      properties["platform"] = platform;
      properties["nextJsWebsite"] = true;
      properties["device_screen_type"] = deviceScreenType;
      properties[EventPropertiesEnum.PAGE_URL] = window.location.href;
      // Attach fbp (Meta Pixel) identifier
      const { fbc, fbp } = getOrSetFbIdentifiers(cookieLookup);
      if (fbc) {
        properties[EventPropertiesEnum.FBC] = fbc;
      }
      if (fbp) {
        properties[EventPropertiesEnum.FBP] = fbp;
      }

      // Add Statsig experiment values to properties for tracked events
      if (statsigEvents.includes(eventName as EventNamesEnum)) {
        Object.entries(statsigExperimentValues).forEach(
          ([experimentName, experimentParams]) => {
            const filteredParams: Record<string, unknown> = {};

            // Filter out empty values
            Object.entries(experimentParams).forEach(
              ([paramName, paramValue]) => {
                if (
                  paramValue !== undefined &&
                  paramValue !== null &&
                  paramValue !== ""
                ) {
                  filteredParams[paramName] = paramValue;
                }
              },
            );

            // Add to properties only if we have non-empty values
            if (Object.keys(filteredParams).length > 0) {
              properties[experimentName] = filteredParams;
            }
          },
        );
      }

      const mandateStatus = cookieLookup(
        cookiesValues[CookiesEnum.MandateStatus],
      );
      const subscriptionStatus = cookieLookup(
        cookiesValues[CookiesEnum.SubscriptionStatus],
      );
      properties[EventPropertiesEnum.MANDATE_STATUS] = mandateStatus;
      properties[EventPropertiesEnum.SUBSCRIPTION_STATUS] = subscriptionStatus;

      // Remove excluded properties per event (before sending to any destination)
      applyPropertyExclusions(eventName, properties);

      //Send To RudderStack and GTM by default
      if (!restrictDefault) {
        const sendEvents = () => {
          // ref: https://stagedotin.slack.com/archives/C09FNE3CHDL/p1758611433502439
          // sendGtmEvent({ eventName, properties });
          sendRudderStackEvent({ eventName, properties });
          if (!isTvEnv()) {
            // this blocks the main thread, so we are not sending firebase events in case of tv
            sendFirebaseEvent({ eventName, properties });
          }
        };
        if (typeof window !== "undefined" && window.requestIdleCallback) {
          window.requestIdleCallback(sendEvents);
        } else {
          sendEvents();
        }
      }

      if (
        clarityEvents.includes(eventName as EventNamesEnum) &&
        window.clarity
      ) {
        if (
          (eventName === EventNamesEnum.TRIAL_PAYMENT_PAGE_VIEWED ||
            eventName === EventNamesEnum.SUBSCRIPTION_PAYMENT_PAGE_VIEWED) &&
          isMobile
        ) {
          window.clarity(
            "event",
            getAppendedPlatformEventName(
              properties?.payment_option_displayed as string,
              properties?.platform as string,
            ),
          );
        }

        window.clarity(
          "event",
          getAppendedPlatformEventName(
            eventName +
              (eventName === EventNamesEnum.CAMPAIGN_CARD_CLICKED
                ? `(${properties?.page_name})`
                : ""),
            properties?.platform as string,
          ),
        );
      }

      if (
        appsflyerEvents.includes(eventName as EventNamesEnum) &&
        window.AF &&
        !isTvEnv()
      ) {
        const afEventName = getAppendedPlatformEventName(
          eventName,
          properties?.platform as string,
        );
        window.AF("pba", "event", {
          eventType: "EVENT",
          eventName: afEventName,
          eventValue: {
            af_customer_user_id:
              properties[EventPropertiesEnum.CUSTOMER_USER_ID],
            ...properties,
          },
        });

        // Send standard AppsFlyer events (af_add_to_cart, af_initiated_checkout, af_purchase)
        // to match the Flutter app's implementation for proper revenue attribution
        const afCurrency =
          (properties[EventPropertiesEnum.CURRENCY] as string) || "INR";
        const afPrice =
          (properties[EventPropertiesEnum.VALUE] as string) ||
          (properties["price"] as string) ||
          "";
        const afContentId =
          (properties[EventPropertiesEnum.CONTENT_ID] as string) || "";

        if (
          eventName === EventNamesEnum.TRIAL_INITIATED ||
          eventName === EventNamesEnum.SUBSCRIPTION_INITIATED
        ) {
          window.AF("pba", "event", {
            eventType: "EVENT",
            eventName: AppsFlyerStandardEvents.ADD_TO_CART,
            eventValue: {
              af_price: afPrice,
              af_currency: afCurrency,
              af_content_id: afContentId,
              af_customer_user_id:
                properties[EventPropertiesEnum.CUSTOMER_USER_ID],
              plan_id: properties["plan_id"] || "",
            },
          });
        }

        if (
          eventName === EventNamesEnum.TRIAL_PAYMENT_INITIATED ||
          eventName === EventNamesEnum.SUBSCRIPTION_PAYMENT_INITIATED
        ) {
          window.AF("pba", "event", {
            eventType: "EVENT",
            eventName: AppsFlyerStandardEvents.INITIATED_CHECKOUT,
            eventValue: {
              af_price: afPrice,
              af_currency: afCurrency,
              af_content_id: afContentId,
              af_customer_user_id:
                properties[EventPropertiesEnum.CUSTOMER_USER_ID],
              plan_id: properties["plan_id"] || "",
              payment_gateway: properties["payment_gateway"] || "",
              payment_method: properties["payment_app_name"] || "",
            },
          });
        }

        if (eventName === EventNamesEnum.START_TRIAL) {
          const couponCode = sessionStorage.getItem(couponCodeKey) || "";
          window.AF("pba", "event", {
            eventType: "EVENT",
            eventName: AppsFlyerStandardEvents.PURCHASE,
            eventValue: {
              af_revenue: afPrice,
              af_currency: afCurrency,
              af_customer_user_id:
                properties[EventPropertiesEnum.CUSTOMER_USER_ID],
              plan_id: properties["plan_id"] || "",
              ...(couponCode ? { af_coupon_code: couponCode } : {}),
            },
          });
        }
      }

      if (sendTo.includes(UseTrackingAnalyticsSendToEnum.FACEBOOK)) {
        let fbProperties = { ...properties, ...fbOverRide?.properties };
        let fbEventName = fbOverRide?.eventName || eventName;

        await sendFbPixelEvent({
          eventName: fbEventName,
          properties: fbProperties,
        });
      }
    } catch (error) {
      console.warn("Error in tracking event", error);
    }
  };

  const sendFirebaseEvent = ({
    eventName,
    properties,
  }: {
    eventName: string;
    properties: Record<string, unknown>;
  }) => {
    const firebaseAnalytics = window.firebaseAnalytics as Analytics;
    if (!firebaseAnalytics) {
      console.warn("Firebase Analytics not initialized yet", eventName);
      return;
    }
    logEvent(firebaseAnalytics, eventName, properties);
  };

  const sendRudderStackEvent = ({
    eventName,
    properties,
  }: {
    eventName: string;
    properties: Record<string, unknown>;
  }) => {
    const rudderAnalytics = window.rudderanalytics;
    if (!rudderAnalytics) {
      console.warn("RudderAnalytics not initialized yet", eventName);
      return;
    }

    try {
      // Append platform name to eventname for cross-platform analytics tracking.
      // refer: https://stagedotin.slack.com/archives/CEHPPGSN9/p1749717211499119
      rudderAnalytics.track(
        getAppendedPlatformEventName(eventName, properties.platform as string),
        properties,
      );
    } catch (error) {
      console.error(
        "Event failed to track on RudderStack - ",
        eventName,
        " Error - ",
        error,
      );
    }
  };

  const sendGtmEvent = ({
    eventName,
    properties,
  }: {
    eventName: string;
    properties: Record<string, unknown>;
  }) => {
    const gtmDataLayer = window.dataLayer as Window["dataLayer"];
    if (!gtmDataLayer) {
      console.warn("gtmDataLayer not initialized yet", eventName);
      return;
    }

    try {
      gtmDataLayer.push({
        event: eventName,
        properties: { ...properties },
      });
    } catch (error) {
      console.warn("Data not pushed in gtmDataLayer", eventName);
    }
  };

  const identifyUser = (parsedUserObj: UserDataInterface) => {
    // rudderstack
    const rudderAnalytics = window.rudderanalytics;
    if (!rudderAnalytics) {
      console.warn("RudderAnalytics not initialized yet identifyUser");
      return;
    }

    if (window.clarity && parsedUserObj?.userId) {
      window.clarity("identify", parsedUserObj?.userId);
    }

    if (window.AF && parsedUserObj?.userId) {
      window.AF("pba", "setCustomerUserId", parsedUserObj.userId);
    }

    try {
      rudderAnalytics.identify(parsedUserObj?.userId, {
        mobileNumber: parsedUserObj?.mobileNumber,
      });
    } catch (error) {
      console.warn("Error in identifying user");
    }
  };

  // Method to track page view todo - need to implement
  const trackPageView = ({ pageName }: { pageName: string }) => {
    const rudderAnalytics = window.rudderanalytics;
    if (!rudderAnalytics) {
      console.warn(
        "RudderAnalytics not initialized yet cannpt track page view",
      );
      return;
    }

    try {
      rudderAnalytics.page({
        // category: "Category",
        name: pageName,
      });
    } catch (error) {
      console.warn("Error in page view tracking");
    }
  };

  const sendFbPixelEvent = async ({
    eventName,
    properties,
  }: {
    eventName: string;
    properties: Record<string, unknown>;
  }) => {
    // mobile number is removed from fb event because facebook is blocking events that contains sensitive info like phone number
    delete properties?.mobileNumber;

    const fbq = window.fbq;

    fbq("track", eventName, properties);
  };

  return { trackEvent, identifyUser, trackPageView };
};
